import { Tooltip } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React, { useState } from 'react';

import { EFreqCapTimePeriod, IDeliveryVariables } from '~/src/dash/apiV2/forecasting/forecasting';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { SimpleSwitch } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/SimpleSwitch';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  Col,
  Cols,
  IndependentSelect,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { IDeliveryVariablesParams } from '~/src/dash/views/tools/Forecasting/DeliveryVariables';

export const timePeriods = [
  {
    id: EFreqCapTimePeriod.Days,
    label: 'Day(s)',
    isActive: true,
  },
  {
    id: EFreqCapTimePeriod.Hours,
    label: 'Hour(s)',
    isActive: false,
  },
] as ISelectOption[];

export const FrequencyCapping = ({
  deliveryVariables,
  setDeliveryVariables,
}: IDeliveryVariablesParams): JSX.Element => {
  const [isEnabled, setIsEnabled] = useState(deliveryVariables.FreqCapActive);
  const [timePeriodOptions, setTimePeriodOptions] = useState(timePeriods);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(
    timePeriodOptions.find((option) => option.isActive),
  );

  const handleApplySelectedTimePeriod = (set: Set<string | number>) => {
    const newTimePeriods = cloneDeep(timePeriods);
    newTimePeriods.forEach((period) => (period.isActive = false));
    let selectedID = EFreqCapTimePeriod.Days;
    // set contains only 1 value, set the selectedID to be equal to that value
    set.forEach((val) => (selectedID = val as number));
    newTimePeriods.forEach((period) => {
      if (period.id === selectedID) {
        period.isActive = true;
      }
    });
    setTimePeriodOptions(newTimePeriods);
    setSelectedTimePeriod(newTimePeriods.find((option) => option.isActive));
    deliveryVariables.FreqCapTimePeriod = selectedID;
  };

  const handleToggleFreqCap = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setIsEnabled(e.target.checked);
    // clone necessary to trigger rerender on deliveryVariables for Forecast component
    const newDeliveryVariables = { ...deliveryVariables } as IDeliveryVariables;
    newDeliveryVariables.FreqCapActive = e.target.checked;
    setDeliveryVariables(newDeliveryVariables);
  };

  return (
    <Rows gap={spacing.base}>
      <Row noPadding>
        <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
          <Col>
            <Text bold small>
              {isEnabled ? 'Active' : 'Inactive'}
            </Text>
          </Col>
          <Col>
            <SimpleSwitch
              isChecked={isEnabled}
              handleSwitch={handleToggleFreqCap}
              key='FrequencyCapping'
              inputID='frequency_capping_enabled_toggle'
            />
          </Col>
          <Col>
            <Tooltip
              title='This variable will limit opportunities to one impression per pod.'
              placement='top'
            >
              <div style={{ display: 'flex' }}>
                <IconV2 large name='info-static' color={publicaColors.gray} />
              </div>
            </Tooltip>
          </Col>
        </Cols>
      </Row>
      {isEnabled && (
        <Row noPadding>
          <Cols
            gap={spacing.extraSmall}
            style={{
              alignItems: 'center',
            }}
          >
            <Col>
              <Text small>
                Set <Span bold>frequency</Span> limit to
              </Text>
            </Col>
            <Col>
              <TextInput
                width='48px'
                placeholder='0'
                initialValue={String(deliveryVariables.FreqCapImps || '')}
                msDelay={500}
                handleOnChange={(value: string) =>
                  (deliveryVariables.FreqCapImps = parseInt(value, 10))
                }
              />
            </Col>
            <Col>
              <Text small>impressions per user per</Text>
            </Col>
            <Col>
              <TextInput
                width='48px'
                placeholder='0'
                initialValue={String(deliveryVariables.FreqCapTimePeriodCount || '')}
                msDelay={500}
                handleOnChange={(value: string) =>
                  (deliveryVariables.FreqCapTimePeriodCount = parseInt(value, 10))
                }
              />
            </Col>
            <Col>
              <IndependentSelect
                key='timePeriod'
                placeholder=''
                listLabel=''
                selectedLimit={1}
                options={timePeriodOptions}
                selectedOptionIDs={
                  selectedTimePeriod
                    ? new Set([selectedTimePeriod.id])
                    : new Set([timePeriods[0].id])
                }
                singularLabel='time period'
                handleApplySelect={handleApplySelectedTimePeriod}
                minWidth={60}
                popoverWidth={152}
                height={28}
                isSelectionRequired={true}
                disableSearch={true}
                isCheckboxHidden={true}
              />
            </Col>
          </Cols>
        </Row>
      )}
    </Rows>
  );
};
